import React, { useState, useRef } from 'react';
import LazyImage from './LazyImage';

/**
 * ImageSlider optimized to support smooth touch/mouse horizontal sliding & dragging.
 */
export default function ImageSlider({ images, alt = "Course Image", className = "" }) {
  const [currentIdx, setCurrentIdx] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  const containerRef = useRef(null);
  const startXRef = useRef(0);
  const startYRef = useRef(0);
  const isDraggingRef = useRef(false);
  const isSwiping = useRef(false);

  if (!images || images.length === 0) {
    return <div className={`bg-slate-100 flex items-center justify-center ${className}`}>No Image</div>;
  }

  const handlePrev = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setCurrentIdx((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleNext = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setCurrentIdx((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const handleDotClick = (e, index) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentIdx(index);
  };

  const handleDragStart = (e, clientX, clientY) => {
    // Only drag with left click for mouse events
    if (e.type.startsWith('mouse') && e.button !== 0) return;

    isDraggingRef.current = true;
    setIsDragging(true);
    isSwiping.current = false;
    startXRef.current = clientX;
    startYRef.current = clientY;
    setDragOffset(0);
  };

  const handleDragMove = (e, clientX, clientY) => {
    if (!isDraggingRef.current) return;

    const diffX = clientX - startXRef.current;
    const diffY = clientY - startYRef.current;

    // Check if horizontal movement is stronger than vertical scroll
    if (Math.abs(diffX) > 10 || Math.abs(diffY) > 10) {
      if (Math.abs(diffX) > Math.abs(diffY)) {
        // Prevent default scrolling behavior on mobile when horizontal dragging is dominant
        if (e.cancelable) {
          e.preventDefault();
        }
        isSwiping.current = true;
        setDragOffset(diffX);
      }
    }
  };

  const handleDragEnd = () => {
    if (!isDraggingRef.current) return;
    isDraggingRef.current = false;
    setIsDragging(false);

    const minSwipeDistance = 50;

    if (isSwiping.current && Math.abs(dragOffset) > minSwipeDistance) {
      if (dragOffset < 0) {
        handleNext();
      } else {
        handlePrev();
      }
    }

    setDragOffset(0);
    startXRef.current = 0;
    startYRef.current = 0;
  };

  const handleClick = (e) => {
    // Prevent navigating/clicking link inside the slider if we were just swiping/dragging
    if (isSwiping.current) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const onMouseDown = (e) => handleDragStart(e, e.clientX, e.clientY);
  const onMouseMove = (e) => handleDragMove(e, e.clientX, e.clientY);
  const onMouseUp = () => handleDragEnd();
  const onMouseLeave = () => handleDragEnd();

  const onTouchStart = (e) => {
    const touch = e.touches[0];
    if (touch) {
      handleDragStart(e, touch.clientX, touch.clientY);
    }
  };
  const onTouchMove = (e) => {
    const touch = e.touches[0];
    if (touch) {
      handleDragMove(e, touch.clientX, touch.clientY);
    }
  };
  const onTouchEnd = () => handleDragEnd();

  return (
    <div 
      className={`relative group overflow-hidden select-none ${className}`}
      style={{ touchAction: 'pan-y' }}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      onMouseLeave={onMouseLeave}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onClick={handleClick}
    >
      {/* Images flex container */}
      <div 
        ref={containerRef}
        className="w-full h-full flex"
        style={{
          transform: `translateX(calc(-${currentIdx * 100}% + ${dragOffset}px))`,
          transition: isDragging ? 'none' : 'transform 500ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
        }}
      >
        {images.map((img, idx) => (
          <div
            key={idx}
            className="w-full h-full flex-shrink-0 relative select-none"
          >
            {/* Blurred background to prevent empty margins */}
            <div 
              className="absolute inset-0 bg-cover bg-center blur-md opacity-35 scale-105 pointer-events-none"
              style={{ backgroundImage: `url(${img})` }}
            />
            {/* Sharp centered foreground image without crop */}
            <LazyImage
              src={img}
              alt={`${alt} view ${idx + 1}`}
              className="relative w-full h-full z-10 bg-transparent pointer-events-none"
              placeholderBg="bg-transparent"
              objectFit="contain"
            />
          </div>
        ))}
      </div>

      {/* Navigation Arrows (shown on hover on desktop) */}
      {images.length > 1 && (
        <>
          <button
            onClick={handlePrev}
            aria-label="Previous slide"
            className="absolute left-3 top-1/2 -translate-y-1/2 z-20 w-8 h-8 rounded-full bg-white/80 backdrop-blur-md border border-white/20 flex items-center justify-center text-slate-700 hover:bg-[#0B1C2D] hover:text-white transition-all duration-300 opacity-0 group-hover:opacity-100 shadow-md transform hover:scale-105 active:scale-95 cursor-pointer"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            onClick={handleNext}
            aria-label="Next slide"
            className="absolute right-3 top-1/2 -translate-y-1/2 z-20 w-8 h-8 rounded-full bg-white/80 backdrop-blur-md border border-white/20 flex items-center justify-center text-slate-700 hover:bg-[#0B1C2D] hover:text-white transition-all duration-300 opacity-0 group-hover:opacity-100 shadow-md transform hover:scale-105 active:scale-95 cursor-pointer"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </>
      )}

      {/* Dot Indicators */}
      {images.length > 1 && (
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-20 flex space-x-1.5 bg-black/25 backdrop-blur-xs px-2.5 py-1.5 rounded-full">
          {images.map((_, idx) => (
            <button
              key={idx}
              onClick={(e) => handleDotClick(e, idx)}
              aria-label={`Go to slide ${idx + 1}`}
              className={`w-1.5 h-1.5 rounded-full transition-all duration-300 cursor-pointer ${
                idx === currentIdx ? 'bg-white w-3' : 'bg-white/50'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
